import { CloseRounded as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Drawer,
  Typography,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import * as dates from "common/dates";
import * as numbers from "common/numbers";
import Button from "components/common/Button";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import IconButton from "components/common/IconButton";
import {
  TYPE_ADDITIONAL,
  TYPE_RETURNED,
} from "components/layers/ActiveLayerPremiumAdjustmentsEditor";
import * as labels from "config.labels";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useSidebar } from "store/hooks/layoutHooks";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const nilCalculations = {
  totalDays: 0,
  newPolicyLength: 0,
  policyLengthChange: 0,
  remainingFractionOfPolicy: 0,
  changedFractionOfPolicy: 0,
  remainingPremium: 0,
  changeInPremium: 0,
};

const ProRataCalculator = ({
  inception,
  expiry,
  premium,
  isOpen,
  onClose,
  addPremiumAdjustment,
}) => {
  const { top } = useSidebar(isOpen);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const TITLE = translateLabel(labels.PRO_RATA_CALC_TITLE, {
    default: "Pro Rata Calculator",
  });
  const POLICY_INFORMATION = translateLabel(
    labels.PRO_RATA_CALC_POLICY_INFORMATION,
    { default: "Policy Information" }
  );
  const TOTAL_PREMIUM = translateLabel(labels.PRO_RATA_CALC_TOTAL_PREMIUM, {
    default: "Total Premium",
  });
  const INCEPTION_DATE = translateLabel(labels.PRO_RATA_CALC_INCEPTION_DATE, {
    default: "Inception Date",
  });
  const EXPIRY_DATE = translateLabel(labels.PRO_RATA_CALC_EXPIRY_DATE, {
    default: "Expiry Date",
  });
  const NEW_CLOSE_DATE = translateLabel(labels.PRO_RATA_CALC_NEW_CLOSE_DATE, {
    default: "New Close Date",
  });
  const POLICY_PERIOD = translateLabel(labels.PRO_RATA_CALC_POLICY_PERIOD, {
    default: "Policy Period",
  });
  const TOTAL_POLICY_DAYS = translateLabel(
    labels.PRO_RATA_CALC_TOTAL_POLICY_DAYS,
    { default: "Total Policy Days" }
  );
  const UPDATED_POLICY_DAYS = translateLabel(
    labels.PRO_RATA_CALC_UPDATED_POLICY_DAYS,
    { default: "Updated Policy Days" }
  );
  const TOGGLE_ADDITIONAL_TITLE = translateLabel(
    labels.PRO_RATA_CALC_ADDITIONAL_TITLE,
    { default: "Additional Premium" }
  );
  const TOGGLE_RETURNED_TITLE = translateLabel(
    labels.PRO_RATA_CALC_RETURNED_TITLE,
    { default: "Returned Premium" }
  );
  const PREMIUM_INCREASED = translateLabel(
    labels.PRO_RATA_CALC_PREMIUM_INCREASED,
    { default: "Premium increased by" }
  );
  const PREMIUM_REDUCED = translateLabel(labels.PRO_RATA_CALC_PREMIUM_REDUCED, {
    default: "Premium Reduced by",
  });
  const PREMIUM_TITLE = translateLabel(labels.PRO_RATA_CALC_PREMIUM_TITLE, {
    default: "Premium Breakdown",
  });
  const FACTOR_TITLE = translateLabel(labels.PRO_RATA_CALC_FACTOR_TITLE, {
    default: "Factors",
  });
  const FACTOR_REDUCTION = translateLabel(
    labels.PRO_RATA_CALC_FACTOR_REDUCTION,
    { default: "Reduction Factor" }
  );
  const FACTOR_ADDITION = translateLabel(labels.PRO_RATA_CALC_FACTOR_ADDITION, {
    default: "Increase Factor",
  });
  const FACTOR_REMAINING = translateLabel(
    labels.PRO_RATA_CALC_FACTOR_REMAINING,
    { default: "Total Factor" }
  );
  const DAYS_EXTENDED = translateLabel(labels.PRO_RATA_CALC_DAYS_EXTENDED, {
    default: "Days Extended",
  });
  const DAYS_REDUCED = translateLabel(labels.PRO_RATA_CALC_DAYS_REDUCED, {
    default: "Days Reduced",
  });
  const TOTAL_NEW_PREMIUM = translateLabel(
    labels.PRO_RATA_CALC_TOTAL_NEW_PREMIUM,
    { default: "Total New Premium" }
  );

  const getDefaultCancelDate = useCallback(() => {
    const today = dates.getDate().toISOString().substring(0, 10);
    return dates.strDateInRange(today, inception, expiry) ? today : null;
  }, [inception, expiry]);

  const [isAdditional, setIsAdditional] = useState(true);
  const [newEndDate, setNewEndDate] = useState(getDefaultCancelDate());
  const [calculations, setCalculations] = useState(nilCalculations);

  useEffect(() => {
    setNewEndDate(getDefaultCancelDate());
  }, [getDefaultCancelDate]);

  useEffect(() => {
    const calc = { ...nilCalculations };

    if (inception && expiry) {
      const inceptionDate = dayjs(inception);
      const expiryDate = dayjs(expiry);
      calc.totalDays = expiryDate.diff(inceptionDate, "day");

      if (newEndDate) {
        calc.newPolicyLength = dayjs(newEndDate).diff(inceptionDate, "day");
        calc.policyLengthChange = calc.totalDays - calc.newPolicyLength;
        const extending = calc.policyLengthChange < 0;
        if (calc.policyLengthChange !== 0 && extending !== isAdditional)
          setIsAdditional(extending);
        calc.remainingFractionOfPolicy = calc.newPolicyLength / calc.totalDays;
        calc.changedFractionOfPolicy = 1 - calc.remainingFractionOfPolicy;

        calc.remainingPremium = (premium ?? 0) * calc.remainingFractionOfPolicy;
        calc.changeInPremium = (premium ?? 0) * calc.changedFractionOfPolicy;
      }
    }

    setCalculations(calc);
  }, [inception, expiry, premium, newEndDate, isAdditional]);

  const percentageFormat = numbers.percentage(2);

  const createAdditionalPremiumEntry = () => {
    addPremiumAdjustment(
      isAdditional ? TYPE_ADDITIONAL : TYPE_RETURNED,
      newEndDate,
      -calculations.changeInPremium
    );
  };

  return (
    <Drawer
      open={isOpen}
      variant={"persistent"}
      anchor={"right"}
      onClose={onClose}
      PaperProps={{
        className: "sidebar",
        sx: {
          top: `${top}px`,
          height: `calc(100vh - ${top}px)`,
          width: 400,
        },
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{ padding: "0.5rem 0.5rem 0.5rem 1rem" }}
      >
        <Typography variant={"h6"}>{TITLE}</Typography>
        <IconButton icon={CloseIcon} className={"link"} onClick={onClose} />
      </Stack>
      <Stack
        direction={"row"}
        alignItems={"center"}
        display={"flex"}
        justifyContent={"space-around"}
      >
        <ToggleButtonGroup
          value={isAdditional ? "Additional" : "Returned"}
          onChange={() => {
            setIsAdditional(!isAdditional);
            setNewEndDate(expiry);
          }}
          color={"primary"}
          exclusive
        >
          <ToggleButton value={"Additional"}>
            {TOGGLE_ADDITIONAL_TITLE}
          </ToggleButton>
          <ToggleButton value={"Returned"}>
            {TOGGLE_RETURNED_TITLE}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ padding: "1rem", overflow: "auto", flex: 1 }}>
        <Stack spacing={3}>
          <Stack spacing={2}>
            <Typography variant={"subtitle2"}>{POLICY_INFORMATION}</Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {TOTAL_PREMIUM}
              </Typography>
              <Typography variant={"body1"}>
                {numbers.currencyFormat(premium || 0)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {INCEPTION_DATE}
              </Typography>
              <Typography variant={"body1"}>
                {dayjs(inception).format("YYYY-MM-DD")}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {EXPIRY_DATE}
              </Typography>
              <Typography variant={"body1"}>
                {dayjs(expiry).format("YYYY-MM-DD")}
              </Typography>
            </Box>

            <FormattedDatePicker
              label={NEW_CLOSE_DATE}
              value={newEndDate}
              onChangeDate={setNewEndDate}
              minDate={isAdditional ? dayjs(expiry) : dayjs(inception)}
              maxDate={isAdditional ? null : dayjs(expiry)}
            />
          </Stack>

          <Divider />

          <Stack gap={1}>
            <Typography variant={"subtitle2"}>{POLICY_PERIOD}</Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {TOTAL_POLICY_DAYS}
              </Typography>
              <Typography variant={"body1"}>
                {calculations.totalDays}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {UPDATED_POLICY_DAYS}
              </Typography>
              <Typography variant={"body1"}>
                {calculations.newPolicyLength}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {isAdditional ? DAYS_EXTENDED : DAYS_REDUCED}
              </Typography>
              <Typography variant={"body1"}>
                {Math.abs(calculations.policyLengthChange)}
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack gap={1}>
            <Typography variant={"subtitle2"}>{FACTOR_TITLE}</Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {FACTOR_REMAINING}
              </Typography>
              <Typography
                variant={"body1"}
                color={"success.main"}
                fontWeight={"medium"}
              >
                {percentageFormat(calculations.remainingFractionOfPolicy)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {isAdditional ? FACTOR_ADDITION : FACTOR_REDUCTION}
              </Typography>
              <Typography
                variant={"body1"}
                color={"warning.main"}
                fontWeight={"medium"}
              >
                {percentageFormat(
                  Math.abs(calculations.changedFractionOfPolicy)
                )}
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack gap={1}>
            <Typography variant={"subtitle2"}>{PREMIUM_TITLE}</Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {TOTAL_NEW_PREMIUM}
              </Typography>
              <Typography
                variant={"body1"}
                color={"success.main"}
                fontWeight={"medium"}
              >
                {numbers.currencyFormat(calculations.remainingPremium)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant={"body2"} color={"text.secondary"}>
                {isAdditional ? PREMIUM_INCREASED : PREMIUM_REDUCED}
              </Typography>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Typography
                  variant={"body1"}
                  color={"warning.main"}
                  fontWeight={"medium"}
                >
                  {numbers.currencyFormat(
                    Math.abs(calculations.changeInPremium)
                  )}
                </Typography>
              </Stack>
            </Box>
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <Typography
                variant={"body1"}
                color={"warning.main"}
                fontWeight={"medium"}
              >
                {"Premium adjustment"}
              </Typography>
              <Button onClick={createAdditionalPremiumEntry}>
                {isAdditional ? "Add Additional" : "Add Returned"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default ProRataCalculator;
